import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SpatialPlanWizard } from "@/features/feasibility/SpatialPlanWizard";
import { setMunicipalityOnDraft } from "@/features/project-data/prefillToDraft";

/**
 * R4 — the plan-upload flow brought to WHERE the plan is missing.
 *
 * The plan-missing banner (R1) used to just link back to the landing.
 * This wraps the existing ``SpatialPlanWizard`` in a modal so the
 * architect uploads, reviews and activates the plan without leaving the
 * project — and on activation the new municipality slug is written onto
 * the project draft, so the very next generate cites the real plan
 * instead of the "[PLAN NIJE U BAZI]" placeholder.
 *
 * Nothing about the wizard/intake itself changes — this is only an entry
 * point + the draft wiring + an "activated" callback the caller uses to
 * offer regeneration.
 */
export function PlanUploadDialog({
  open,
  onOpenChange,
  projectId,
  initialJlsName,
  onActivated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Project whose draft gets the activated municipality slug. */
  projectId?: string;
  /** Prefill for the wizard's JLS field (usually the k.o. name). */
  initialJlsName?: string;
  /** Fired after activation with the live slug + name, so the caller can
   *  offer document regeneration. */
  onActivated?: (code: string, name: string) => void;
}) {
  const handleActivated = (code: string, name: string) => {
    // Point the project at the freshly activated municipality so the next
    // generation reads its plan (R1's guard now resolves instead of
    // placeholdering).
    if (projectId) setMunicipalityOnDraft(projectId, code);
    onActivated?.(code, name);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        // The wizard has its own close affordance in some phases; keep the
        // dialog's X too so the architect can always back out.
      >
        <DialogHeader>
          <DialogTitle>Učitaj prostorni plan</DialogTitle>
          <DialogDescription>
            Ubaci sve što imaš od plana (odredbe, izmjene, pročišćeni tekst,
            karte) — sustav prepozna što je što, izvuče limite s citatima
            članaka, ti ih potvrdiš i općina je odmah spremna za provjeru i
            generiranje.
          </DialogDescription>
        </DialogHeader>
        <SpatialPlanWizard
          initialJlsName={initialJlsName}
          onActivated={handleActivated}
          onClose={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}
